import { useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard, BarChart3, Globe, Server,
  ChevronLeft, ChevronRight as Expand, User2, LogOut,
  History,
  // Icon map — all icons the backend catalogue may reference
  MapPin, Radio, Clock, Route, Terminal,
  Bell, SlidersHorizontal, Tag,
  Truck, List, Cpu, Layers, WalletCards, RefreshCw,
  Globe2, CreditCard, BarChart2, FileText,
  Wrench, Package, Zap, Share2, Settings,
  Users, UserCheck, ShieldCheck, KeyRound,
  Briefcase, CheckSquare, Calendar, Send,
  DollarSign, BookOpen, Map, AlertCircle,
  Heart, Battery, Link2, Repeat, Grid3X3,
  Building2, Receipt, Wallet, Car, School,
  PawPrint, Bus, Pentagon,
} from 'lucide-react'
import { useUIStore } from '@/store/ui'
import { useAuthStore } from '@/store/auth'
import { useTenantStore } from '@/store/tenant'
import { getMenu, type MenuItem } from '@/api/menu'
import type { PermissionMap } from '@/types'
import { cn } from '@/utils/cn'

// ── Icon resolver ──────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ElementType> = {
  'history': History,
  'map-pin': MapPin,
  'radio': Radio,
  'clock': Clock,
  'route': Route,
  'terminal': Terminal,
  'server': Server,
  'bell': Bell,
  'sliders': SlidersHorizontal,
  'tag': Tag,
  'pentagon': Pentagon,
  'truck': Truck,
  'list': List,
  'cpu': Cpu,
  'layers': Layers,
  'sim-card': WalletCards,
  'refresh': RefreshCw,
  'globe': Globe,
  'globe2': Globe2,
  'credit-card': CreditCard,
  'bar-chart': BarChart3,
  'bar-chart-2': BarChart2,
  'file-text': FileText,
  'tool': Wrench,
  'package': Package,
  'zap': Zap,
  'share-2': Share2,
  'settings': Settings,
  'users': Users,
  'user-check': UserCheck,
  'shield': ShieldCheck,
  'key': KeyRound,
  'briefcase': Briefcase,
  'check-square': CheckSquare,
  'calendar': Calendar,
  'send': Send,
  'dollar-sign': DollarSign,
  'book': BookOpen,
  'map': Map,
  'bus': Bus,
  'alert-circle': AlertCircle,
  'heart': Heart,
  'battery': Battery,
  'link': Link2,
  'repeat': Repeat,
  'grid': Grid3X3,
  'building': Building2,
  'receipt': Receipt,
  'wallet': Wallet,
  'car': Car,
  'school': School,
  'paw-print': PawPrint,
}

function resolveIcon(name: string): React.ElementType {
  return ICON_MAP[name] ?? Settings
}

// ── Route map: backend menu key → frontend route ───────────────────────────────

const ROUTE_MAP: Record<string, string> = {
  // Tracking
  'tracking.live':     '/map',
  'tracking.history':  '/history',
  'tracking.trips':    '/trips',
  'tracking.packets':  '/packets',
  // Alerts
  'alerts.list':       '/alerts',
  'alerts.manage':     '/alerts',
  'alerts.types':      '/admin/alert-types',
  // Geofences
  'geofences.list':            '/geofences',
  'geofences.events':          '/geofences',
  'geofences.assign_vehicles': '/geofences',
  // Vehicles
  'vehicles.list':          '/vehicles',
  'vehicles.assign_device': '/vehicles',
  'vehicles.types':         '/admin/vehicle-types',
  // Drivers
  'drivers.list':           '/drivers',
  'drivers.assign_vehicle': '/drivers',
  // Devices
  'devices.list':  '/devices',
  'devices.types': '/devices',
  // SIMs
  'sims.list':      '/sims',
  'sims.sync':      '/sims',
  'sims.providers': '/sims',
  // Billing
  'billing.overview':            '/billing/subscriptions',
  'billing.profiles':            '/billing/profile',
  'billing.profile':             '/billing/profile',
  'billing.manage_profiles':     '/billing/profile',
  'billing.subscription':        '/billing/subscriptions',
  'billing.subscriptions':       '/billing/subscriptions',
  'billing.manage_subscription': '/billing/subscriptions',
  'billing.entities':            '/billing/subscriptions',
  'billing.manage_entities':     '/billing/subscriptions',
  'billing.invoices':            '/billing/invoices',
  'billing.manage_invoices':     '/billing/invoices',
  'billing.payments':            '/billing/payments',
  'billing.manage_payments':     '/billing/payments',
  'billing.wallet':              '/billing/wallet',
  'billing.manage_wallet':       '/billing/wallet',
  'billing.plans':               '/billing/plans',
  'billing.platform_plans':      '/billing/plans',
  'billing.domain_plans':        '/billing/domain-plans',
  'billing.manage_domain_plans': '/billing/domain-plans',
  'billing.gateways':            '/billing/gateways',
  'billing.manage_gateways':     '/billing/gateways',
  // Technician
  'technician.stock':    '/admin/notifications',
  'technician.activate': '/admin/notifications',
  'technician.handover': '/admin/notifications',
  // Field Force
  'fieldforce.list':       '/field-force/employees',
  'fieldforce.tasks':      '/field-force/tasks',
  'fieldforce.attendance': '/field-force/attendance',
  // Dispatch
  'dispatch.list':     '/dispatch/bookings',
  'dispatch.services': '/dispatch/service-types',
  'dispatch.finance':  '/dispatch/bookings',
  // School
  'school.list':       '/school/schools',
  'school.students':   '/school/students',
  'school.attendance': '/school/attendance',
  'school.routes':     '/school/routes',
  // Transport
  'transport.list':   '/transport/routes',
  'transport.roster': '/transport/rosters',
  'transport.sos':    '/transport/sos',
  // Pets
  'pets.list':   '/pets',
  'pets.breeds': '/pets',
  // Batteries / EV
  'batteries.list':   '/ev/batteries',
  'batteries.assign': '/ev/batteries',
  'batteries.swap':   '/ev/swaps',
  // EV Stations
  'ev_stations.manage': '/ev/stations',
  // Reports (many possible backend keys)
  'reports.list':           '/reports',
  'reports.view':           '/reports',
  'reports.all':            '/reports',
  'reports.generate':       '/reports',
  'reports.trips':          '/reports',
  'reports.trip':           '/reports',
  'reports.speed':          '/reports',
  'reports.distance':       '/reports',
  'reports.mileage':        '/reports',
  'reports.ignition':       '/reports',
  'reports.geofence':       '/reports',
  'reports.geofence_events': '/reports',
  'reports.alerts':         '/reports',
  'reports.alert':          '/reports',
  'reports.summary':        '/reports',
  'reports.manage':         '/reports',

  // System (super admin)
  'system.services': '/system',
  'system.parsers':  '/system/parsers',

  // Admin
  'admin.users':       '/admin/users',
  'admin.user_types':  '/admin/user-types',
  'admin.permissions': '/admin/user-types',
  'admin.data_brokers': '/admin/data-brokers',
}

// ── Label overrides — force specific display labels regardless of API value ────
const LABEL_OVERRIDE: Record<string, string> = {
  'tracking.live':    'Live',
  'tracking.history': 'GPS History',
  'tracking.trips':   'Trips',
  // All reports.* keys that link to /reports → rename to "On-Road Summary"
  'reports.list':     'On-Road Summary',
  'reports.view':     'On-Road Summary',
  'reports.all':      'On-Road Summary',
  'reports.generate': 'On-Road Summary',
  'reports.trips':    'On-Road Summary',
  'reports.trip':     'On-Road Summary',
  'reports.summary':  'On-Road Summary',
  'reports.manage':   'On-Road Summary',
  'reports.mileage':  'On-Road Summary',
  'reports.distance': 'On-Road Summary',
  'reports.speed':    'On-Road Summary',
  'reports.ignition': 'On-Road Summary',
  'reports.geofence': 'On-Road Summary',
  'reports.alerts':   'On-Road Summary',
  'reports.alert':    'On-Road Summary',
}

// ── Post-process menu from /menu API ──────────────────────────────────────────
// 1. Apply label overrides
// 2. Move tracking.history + tracking.trips out of Tracking → into Reports
function postProcessMenu(groups: MenuItem[]): MenuItem[] {
  const MOVE_TO_REPORTS = new Set(['tracking.history', 'tracking.trips'])

  // First pass: collect items to promote to Reports
  const promotedItems: MenuItem[] = []
  groups.forEach((g) => {
    if (g.key === 'tracking') {
      ;(g.children ?? []).forEach((c) => {
        if (MOVE_TO_REPORTS.has(c.key)) promotedItems.push(c)
      })
    }
  })

  return groups.map((group) => {
    const applyOverride = (c: MenuItem): MenuItem => ({
      ...c,
      label: LABEL_OVERRIDE[c.key] ?? c.label,
    })

    if (group.key === 'tracking') {
      return {
        ...group,
        children: (group.children ?? [])
          .filter((c) => !MOVE_TO_REPORTS.has(c.key))
          .map(applyOverride),
      }
    }

    if (group.key === 'reports') {
      return {
        ...group,
        children: [
          ...promotedItems.map(applyOverride),
          ...(group.children ?? []).map((c) => {
            // Any child that routes to /reports → rename to "On-Road Summary"
            const route = ROUTE_MAP[c.key]
            if (route === '/reports') return { ...c, label: 'On-Road Summary' }
            return applyOverride(c)
          }),
        ],
      }
    }

    return { ...group, children: (group.children ?? []).map(applyOverride) }
  })
}

// ── Group-level route fallback ─────────────────────────────────────────────────
// If a child key from the backend isn't in ROUTE_MAP, use the group's base route.
// This prevents entire menu groups from disappearing due to unknown child keys.
const GROUP_ROUTE_FALLBACK: Record<string, string> = {
  reports:    '/reports',
  tracking:   '/map',
  alerts:     '/alerts',
  geofences:  '/geofences',
  vehicles:   '/vehicles',
  drivers:    '/drivers',
  devices:    '/devices',
  sims:       '/sims',
  billing:    '/billing/subscriptions',
  fieldforce: '/field-force/employees',
  school:     '/school/schools',
  transport:  '/transport/routes',
  pets:       '/pets',
  batteries:  '/ev/batteries',
  dispatch:   '/dispatch/bookings',
  admin:      '/admin/users',
  system:     '/system',
}

// ── Nav item component ─────────────────────────────────────────────────────────

function SidebarItem({
  to, label, icon: Icon, collapsed,
}: {
  to: string; label: string; icon: React.ElementType; collapsed: boolean
}) {
  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-primary/10 text-primary font-semibold'
            : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-sidebar-active-fg'
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  )
}

// ── Section label ──────────────────────────────────────────────────────────────

function SectionLabel({ label, collapsed }: { label: string; collapsed: boolean }) {
  if (collapsed) return <div className="my-2 border-t border-border" />
  return (
    <p className="mt-5 mb-1.5 px-3 text-[10px] font-bold uppercase tracking-widest text-muted">
      {label}
    </p>
  )
}

// ── Menu group renderer ────────────────────────────────────────────────────────

interface InjectedItem { afterKey: string; to: string; label: string; icon: React.ElementType }

function MenuGroup({
  group, collapsed, inject = [],
}: {
  group: MenuItem
  collapsed: boolean
  inject?: InjectedItem[]
}) {
  const children = group.children ?? []
  if (children.length === 0) return null

  // De-duplicate children by route; fall back to group-level route for unknown keys
  const seen = new Set<string>()
  const unique = children.filter((child) => {
    const route = ROUTE_MAP[child.key] ?? GROUP_ROUTE_FALLBACK[group.key]
    if (!route || seen.has(route)) return false
    seen.add(route)
    return true
  })
  if (unique.length === 0) return null

  // Build final list, inserting injected items after their anchor key
  const items: { key: string; to: string; label: string; icon: React.ElementType }[] = []
  for (const child of unique) {
    items.push({ key: child.key, to: ROUTE_MAP[child.key] ?? GROUP_ROUTE_FALLBACK[group.key] ?? '/', label: child.label, icon: resolveIcon(child.icon) })
    for (const inj of inject) {
      if (inj.afterKey === child.key && !items.some((i) => i.to === inj.to)) {
        items.push({ key: `__inject_${inj.to}`, to: inj.to, label: inj.label, icon: inj.icon })
      }
    }
  }

  return (
    <>
      <SectionLabel label={group.label} collapsed={collapsed} />
      {items.map((item) => (
        <SidebarItem key={item.key} to={item.to} label={item.label} icon={item.icon} collapsed={collapsed} />
      ))}
    </>
  )
}

// ── Fallback menu builder (used when /menu returns empty — backend bug) ────────

function buildFallbackMenu(perms: PermissionMap, isSuperAdmin: boolean): MenuItem[] {
  const can = (r: string) => isSuperAdmin || perms[r]?.canRead === true
  const item = (key: string, label: string, icon: string): MenuItem => ({ key, label, icon })
  const group = (key: string, label: string, icon: string, children: MenuItem[]): MenuItem =>
    ({ key, label, icon, children })

  const groups: MenuItem[] = []

  // Tracking — only Live map stays here
  const trackingItems: MenuItem[] = []
  if (can('tracking') || can('vehicles')) trackingItems.push(item('tracking.live',    'Live',              'map-pin'))
  if (can('devices') || can('tracking'))  trackingItems.push(item('tracking.packets', 'Packet Inspector',  'terminal'))
  if (trackingItems.length) groups.push(group('tracking', 'Tracking', 'map-pin', trackingItems))

  // Alerts
  if (can('alerts'))    groups.push(group('alerts',    'Alerts',    'bell',    [item('alerts.list',    'Alerts',    'bell')]))
  // Geofences
  if (can('geofences')) groups.push(group('geofences', 'Geofences', 'pentagon', [item('geofences.list', 'Geofences', 'pentagon')]))
  // Vehicles
  if (can('vehicles'))  groups.push(group('vehicles',  'Vehicles',  'truck',   [item('vehicles.list', 'Vehicles', 'truck'), item('vehicles.types', 'Vehicle Types', 'layers')]))
  // Drivers
  if (can('drivers'))   groups.push(group('drivers',   'Drivers',   'user-check', [item('drivers.list', 'Drivers', 'user-check')]))
  // Devices
  if (can('devices'))   groups.push(group('devices',   'Devices',   'cpu',    [item('devices.list', 'Devices', 'cpu')]))
  // SIMs
  if (can('sims'))      groups.push(group('sims',      'SIM Cards', 'sim-card', [item('sims.list', 'SIM Cards', 'sim-card')]))

  // Billing
  if (can('billing') || can('billing_subscriptions') || can('billing_plans') || can('billing_invoices')) {
    const billingItems: MenuItem[] = []
    billingItems.push(item('billing.overview',      'Overview',     'credit-card'))
    billingItems.push(item('billing.invoices',      'Invoices',     'receipt'))
    billingItems.push(item('billing.payments',      'Payments',     'dollar-sign'))
    billingItems.push(item('billing.wallet',        'Wallet',       'wallet'))
    billingItems.push(item('billing.domain_plans',  'Domain Plans', 'layers'))
    billingItems.push(item('billing.gateways',      'Gateways',     'credit-card'))
    billingItems.push(item('billing.profiles',      'Profile',      'building'))
    groups.push(group('billing', 'Billing', 'credit-card', billingItems))
  }

  // Technician
  if (can('technician')) {
    groups.push(group('technician', 'Technician', 'tool', [
      item('technician.stock',    'Stock',    'package'),
      item('technician.activate', 'Activate', 'zap'),
      item('technician.handover', 'Handover', 'share-2'),
    ]))
  }

  // Field Force
  if (can('fieldforce')) {
    groups.push(group('fieldforce', 'Field Force', 'briefcase', [
      item('fieldforce.list',       'Employees',  'users'),
      item('fieldforce.tasks',      'Tasks',      'check-square'),
      item('fieldforce.attendance', 'Attendance', 'calendar'),
    ]))
  }

  // School
  if (can('school')) {
    groups.push(group('school', 'School Bus', 'school', [
      item('school.list',       'Schools',    'school'),
      item('school.routes',     'Routes',     'route'),
      item('school.students',   'Students',   'users'),
      item('school.attendance', 'Attendance', 'calendar'),
    ]))
  }

  // Transport
  if (can('transport')) {
    groups.push(group('transport', 'Transport', 'bus', [
      item('transport.list',   'Routes',  'route'),
      item('transport.roster', 'Rosters', 'list'),
    ]))
  }

  // Pets
  if (can('pets')) groups.push(group('pets', 'Pets', 'paw-print', [item('pets.list', 'Pets', 'paw-print')]))

  // EV / Batteries
  if (can('batteries') || can('ev_stations')) {
    const evItems: MenuItem[] = []
    if (can('batteries'))   { evItems.push(item('batteries.list', 'Batteries', 'battery')); evItems.push(item('batteries.swap', 'Swaps', 'repeat')) }
    if (can('ev_stations')) evItems.push(item('ev_stations.manage', 'EV Stations', 'zap'))
    groups.push(group('ev', 'EV Management', 'zap', evItems))
  }

  // Dispatch
  if (can('dispatch')) {
    groups.push(group('dispatch', 'Dispatch', 'send', [
      item('dispatch.list',     'Bookings',      'send'),
      item('dispatch.services', 'Service Types', 'settings'),
    ]))
  }

  // Reports — GPS History + Trips + On-Road Summary
  const reportItems: MenuItem[] = []
  if (can('tracking') || can('vehicles'))  reportItems.push(item('tracking.history', 'GPS History',     'clock'))
  if (can('trips')    || can('tracking'))  reportItems.push(item('tracking.trips',   'Trips',           'route'))
  if (can('reports')  || can('report') || can('reporting'))
                                           reportItems.push(item('reports.list',     'On-Road Summary',  'bar-chart'))
  if (reportItems.length) groups.push(group('reports', 'Reports', 'bar-chart', reportItems))

  // Administration
  const adminItems: MenuItem[] = []
  if (can('users')              || isSuperAdmin) adminItems.push(item('admin.users',        'Users',        'users'))
  if (can('user_types')         || isSuperAdmin) adminItems.push(item('admin.user_types',   'User Types',   'shield'))
  if (can('databroker_configs') || isSuperAdmin) adminItems.push(item('admin.data_brokers', 'Data Brokers', 'share-2'))
  if (adminItems.length) groups.push(group('admin', 'Administration', 'settings', adminItems))

  return groups
}

// ── Main Sidebar ───────────────────────────────────────────────────────────────

export function Sidebar() {
  const { t } = useTranslation('nav')
  const collapsed = useUIStore((s) => s.sidebarCollapsed)
  const toggleSidebar = useUIStore((s) => s.toggleSidebar)
  const theme = useTenantStore((s) => s.theme)
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const is_super = useAuthStore((s) => s.is_super)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const permMap = useAuthStore((s) => s.permissions)
  const permissionsLoaded = useAuthStore((s) => s.permissionsLoaded)

  const { data: menuGroups = [], isLoading } = useQuery({
    queryKey: ['menu'],
    queryFn: getMenu,
    staleTime: 5 * 60_000,
    enabled: isAuthenticated,
  })

  // If /menu returns empty (backend returns nothing for regular users), fall back
  // to building the menu from the locally cached permission map.
  const effectiveGroups = useMemo(() => {
    if (menuGroups.length > 0) return postProcessMenu(menuGroups)
    if (isLoading || !permissionsLoaded) return []
    return buildFallbackMenu(permMap, is_super)
  }, [menuGroups, isLoading, permissionsLoaded, permMap, is_super])

  return (
    <aside
      className={cn(
        'fixed inset-y-0 start-0 z-30 flex flex-col bg-sidebar border-e border-border transition-all duration-200',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo area */}
      <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4">
        {theme?.logoUrl ? (
          <img src={theme.logoUrl} alt={theme.appName} className="h-8 w-auto object-contain" />
        ) : (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-primary text-primary-fg text-xs font-bold">
            {(theme?.appName ?? 'T').slice(0, 2).toUpperCase()}
          </div>
        )}
        {!collapsed && (
          <span className="truncate text-sm font-semibold text-sidebar-active-fg">
            {theme?.appName ?? 'Telematics'}
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {/* Dashboard — always visible */}
        <SidebarItem
          to="/"
          label={t('dashboard')}
          icon={LayoutDashboard}
          collapsed={collapsed}
        />

        {/* Dynamic menu — fully driven by /menu API + permission-based fallback */}
        {isLoading ? (
          !collapsed && (
            <div className="mt-4 space-y-2 px-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="skeleton h-7 rounded" />
              ))}
            </div>
          )
        ) : (
          effectiveGroups.map((group) => (
            <MenuGroup key={group.key} group={group} collapsed={collapsed} />
          ))
        )}

        {/* White Label — super admin only (not yet in /menu API) */}
        {is_super && (
          <SidebarItem
            to="/white-label/domains"
            label={t('whitelabel')}
            icon={Globe}
            collapsed={collapsed}
          />
        )}
      </nav>

      {/* User footer */}
      <div className="shrink-0 border-t border-border p-2">
        <NavLink
          to="/profile"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
              isActive
                ? 'bg-primary/10 text-primary font-semibold'
                : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-sidebar-active-fg'
            )
          }
        >
          <User2 className="h-4 w-4 shrink-0" />
          {!collapsed && (
            <span className="flex-1 truncate">{user?.name ?? t('profile')}</span>
          )}
        </NavLink>

        <button
          onClick={() => logout()}
          className="mt-0.5 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-sidebar-fg transition-colors hover:bg-sidebar-hover hover:text-sidebar-active-fg"
          title={collapsed ? t('logout') : undefined}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span className="truncate">{t('logout')}</span>}
        </button>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={toggleSidebar}
        className="absolute -end-3 top-20 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-surface text-muted shadow-sm hover:text-primary"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <Expand className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </button>
    </aside>
  )
}
